import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { formatPrimaryName } from 'utils/names'
import { BaseSubsectionAdapter } from '../../../adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from '../../../adapters/types'
import { SupervisoryFormData } from '../../types/SupervisoryFormData'
import { SupervisoryFormField } from '../../types/SupervisoryFormField'
import { SupervisoryAssessmentQuestionnaireItem } from '../../types/questionnaire'
import { getAnswerValuesFromItems } from '../../../helpers'

/**
 * Patient Overview subsection adapter
 * Handles demographics and care team fields extraction and transformation
 */
export class PatientOverviewAdapter extends BaseSubsectionAdapter<SupervisoryFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    options?: AdapterOptions
  ): Partial<SupervisoryFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Demographics fields
    const legalName =
      getAnswerValuesFromItems(
        flatAnswersArray,
        SupervisoryAssessmentQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_LEGAL_NAME
      )?.at(0) || undefined

    const preferredName =
      getAnswerValuesFromItems(
        flatAnswersArray,
        SupervisoryAssessmentQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_PREFERRED_NAME
      )?.at(0) || undefined

    const dateOfBirth =
      getAnswerValuesFromItems(
        flatAnswersArray,
        SupervisoryAssessmentQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_DATE_OF_BIRTH
      )?.at(0) || undefined

    const address =
      getAnswerValuesFromItems(
        flatAnswersArray,
        SupervisoryAssessmentQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_ADDRESS
      )?.at(0) || undefined

    const gender =
      getAnswerValuesFromItems(
        flatAnswersArray,
        SupervisoryAssessmentQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_GENDER
      )?.at(0) || undefined

    const sex =
      getAnswerValuesFromItems(
        flatAnswersArray,
        SupervisoryAssessmentQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_SEX
      )?.at(0) || undefined

    const medicaidId =
      getAnswerValuesFromItems(
        flatAnswersArray,
        SupervisoryAssessmentQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_MEDICAID_ID
      )?.at(0) || undefined

    // Care team fields
    const legalGuardianName =
      getAnswerValuesFromItems(
        flatAnswersArray,
        SupervisoryAssessmentQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_LEGAL_GUARDIAN_NAME
      )?.at(0) || undefined

    const legalGuardianRelation =
      getAnswerValuesFromItems(
        flatAnswersArray,
        SupervisoryAssessmentQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_LEGAL_GUARDIAN_RELATION_TO_PATIENT
      )?.at(0) || undefined

    const emergencyContactName =
      getAnswerValuesFromItems(
        flatAnswersArray,
        SupervisoryAssessmentQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_EMERGENCY_CONTACT_NAME
      )?.at(0) || undefined

    const emergencyContactPhone =
      getAnswerValuesFromItems(
        flatAnswersArray,
        SupervisoryAssessmentQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_EMERGENCY_CONTACT_PHONE
      )?.at(0) || undefined

    const emergencyContactAddress =
      getAnswerValuesFromItems(
        flatAnswersArray,
        SupervisoryAssessmentQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_EMERGENCY_CONTACT_ADDRESS
      )?.at(0) || undefined

    const alternateCaregiverName =
      getAnswerValuesFromItems(
        flatAnswersArray,
        SupervisoryAssessmentQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_ALTERNATE_CAREGIVER_NAME
      )?.at(0) || undefined

    const alternateCaregiverPhone =
      getAnswerValuesFromItems(
        flatAnswersArray,
        SupervisoryAssessmentQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_ALTERNATE_CAREGIVER_PHONE
      )?.at(0) || undefined

    const alternateCaregiverAddress =
      getAnswerValuesFromItems(
        flatAnswersArray,
        SupervisoryAssessmentQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_ALTERNATE_CAREGIVER_ADDRESS
      )?.at(0) || undefined

    // Handle caregivers array - complex mapping from care team options
    const names =
      getAnswerValuesFromItems(
        flatAnswersArray,
        SupervisoryAssessmentQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_CAREGIVER_NAME
      ) || []

    const relations =
      getAnswerValuesFromItems(
        flatAnswersArray,
        SupervisoryAssessmentQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_CAREGIVER_RELATION_TO_PATIENT
      ) || []

    let caregivers: Array<{ name: string; relation: string | undefined }> | undefined

    if (options?.careTeam?.caregivers?.length) {
      caregivers = options.careTeam.caregivers.map((caregiver, index) => ({
        name: formatPrimaryName(caregiver.name) || names[index] || '',
        relation: relations[index] || undefined
      }))
    }

    return {
      [SupervisoryFormField.LEGAL_NAME]: legalName,
      [SupervisoryFormField.PREFERRED_NAME]: preferredName,
      [SupervisoryFormField.DATE_OF_BIRTH]: dateOfBirth,
      [SupervisoryFormField.ADDRESS]: address,
      [SupervisoryFormField.GENDER]: gender,
      [SupervisoryFormField.SEX]: sex,
      [SupervisoryFormField.MEDICAID_ID]: medicaidId,
      [SupervisoryFormField.LEGAL_GUARDIAN_NAME]: legalGuardianName,
      [SupervisoryFormField.LEGAL_GUARDIAN_RELATION]: legalGuardianRelation,
      [SupervisoryFormField.EMERGENCY_CONTACT_NAME]: emergencyContactName,
      [SupervisoryFormField.EMERGENCY_CONTACT_PHONE]: emergencyContactPhone,
      [SupervisoryFormField.EMERGENCY_CONTACT_ADDRESS]: emergencyContactAddress,
      [SupervisoryFormField.ALTERNATE_CAREGIVER_NAME]: alternateCaregiverName,
      [SupervisoryFormField.ALTERNATE_CAREGIVER_PHONE]: alternateCaregiverPhone,
      [SupervisoryFormField.ALTERNATE_CAREGIVER_ADDRESS]: alternateCaregiverAddress,
      [SupervisoryFormField.CAREGIVERS]: caregivers
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    // Demographics fields - use formDataFieldExists for optional fields
    if (this.formDataFieldExists(SupervisoryFormField.LEGAL_NAME)) {
      mutations.push({
        linkId: SupervisoryAssessmentQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_LEGAL_NAME,
        value: this.getFormDataField(SupervisoryFormField.LEGAL_NAME)
      })
    }

    if (this.formDataFieldExists(SupervisoryFormField.PREFERRED_NAME)) {
      mutations.push({
        linkId: SupervisoryAssessmentQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_PREFERRED_NAME,
        value: this.getFormDataField(SupervisoryFormField.PREFERRED_NAME)
      })
    }

    if (this.formDataFieldExists(SupervisoryFormField.DATE_OF_BIRTH)) {
      mutations.push({
        linkId: SupervisoryAssessmentQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_DATE_OF_BIRTH,
        value: this.getFormDataField(SupervisoryFormField.DATE_OF_BIRTH)
      })
    }

    if (this.formDataFieldExists(SupervisoryFormField.ADDRESS)) {
      mutations.push({
        linkId: SupervisoryAssessmentQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_ADDRESS,
        value: this.getFormDataField(SupervisoryFormField.ADDRESS)
      })
    }

    if (this.formDataFieldExists(SupervisoryFormField.GENDER)) {
      mutations.push({
        linkId: SupervisoryAssessmentQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_GENDER,
        value: this.getFormDataField(SupervisoryFormField.GENDER)
      })
    }

    if (this.formDataFieldExists(SupervisoryFormField.SEX)) {
      mutations.push({
        linkId: SupervisoryAssessmentQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_SEX,
        value: this.getFormDataField(SupervisoryFormField.SEX)
      })
    }

    if (this.formDataFieldExists(SupervisoryFormField.MEDICAID_ID)) {
      mutations.push({
        linkId: SupervisoryAssessmentQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_MEDICAID_ID,
        value: this.getFormDataField(SupervisoryFormField.MEDICAID_ID)
      })
    }

    // Care team fields
    if (this.formDataFieldExists(SupervisoryFormField.LEGAL_GUARDIAN_NAME)) {
      mutations.push({
        linkId:
          SupervisoryAssessmentQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_LEGAL_GUARDIAN_NAME,
        value: this.getFormDataField(SupervisoryFormField.LEGAL_GUARDIAN_NAME)
      })
    }

    if (this.formDataFieldExists(SupervisoryFormField.LEGAL_GUARDIAN_RELATION)) {
      mutations.push({
        linkId:
          SupervisoryAssessmentQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_LEGAL_GUARDIAN_RELATION_TO_PATIENT,
        value: this.getFormDataField(SupervisoryFormField.LEGAL_GUARDIAN_RELATION)
      })
    }

    if (this.formDataFieldExists(SupervisoryFormField.EMERGENCY_CONTACT_NAME)) {
      mutations.push({
        linkId:
          SupervisoryAssessmentQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_EMERGENCY_CONTACT_NAME,
        value: this.getFormDataField(SupervisoryFormField.EMERGENCY_CONTACT_NAME)
      })
    }

    if (this.formDataFieldExists(SupervisoryFormField.EMERGENCY_CONTACT_PHONE)) {
      mutations.push({
        linkId:
          SupervisoryAssessmentQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_EMERGENCY_CONTACT_PHONE,
        value: this.getFormDataField(SupervisoryFormField.EMERGENCY_CONTACT_PHONE)
      })
    }

    if (this.formDataFieldExists(SupervisoryFormField.EMERGENCY_CONTACT_ADDRESS)) {
      mutations.push({
        linkId:
          SupervisoryAssessmentQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_EMERGENCY_CONTACT_ADDRESS,
        value: this.getFormDataField(SupervisoryFormField.EMERGENCY_CONTACT_ADDRESS)
      })
    }

    if (this.formDataFieldExists(SupervisoryFormField.ALTERNATE_CAREGIVER_NAME)) {
      mutations.push({
        linkId:
          SupervisoryAssessmentQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_ALTERNATE_CAREGIVER_NAME,
        value: this.getFormDataField(SupervisoryFormField.ALTERNATE_CAREGIVER_NAME)
      })
    }

    if (this.formDataFieldExists(SupervisoryFormField.ALTERNATE_CAREGIVER_PHONE)) {
      mutations.push({
        linkId:
          SupervisoryAssessmentQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_ALTERNATE_CAREGIVER_PHONE,
        value: this.getFormDataField(SupervisoryFormField.ALTERNATE_CAREGIVER_PHONE)
      })
    }

    if (this.formDataFieldExists(SupervisoryFormField.ALTERNATE_CAREGIVER_ADDRESS)) {
      mutations.push({
        linkId:
          SupervisoryAssessmentQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_ALTERNATE_CAREGIVER_ADDRESS,
        value: this.getFormDataField(SupervisoryFormField.ALTERNATE_CAREGIVER_ADDRESS)
      })
    }

    // Handle caregivers array - ensure names and relations stay aligned by index
    if (this.formDataFieldExists(SupervisoryFormField.CAREGIVERS)) {
      const caregivers = this.getFormDataField(SupervisoryFormField.CAREGIVERS)
      const validCaregivers = caregivers?.filter(
        (caregiver: { name: string; relation: string | undefined }) =>
          caregiver.name || caregiver.relation
      )

      if (validCaregivers && validCaregivers.length > 0) {
        mutations.push(
          {
            linkId:
              SupervisoryAssessmentQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_CAREGIVER_NAME,
            value: validCaregivers.map(
              (caregiver: { name: string; relation: string | undefined }) => caregiver.name || ''
            )
          },
          {
            linkId:
              SupervisoryAssessmentQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_CAREGIVER_RELATION_TO_PATIENT,
            value: validCaregivers.map(
              (caregiver: { name: string; relation: string | undefined }) =>
                caregiver.relation || ''
            )
          }
        )
      } else {
        mutations.push(
          {
            linkId:
              SupervisoryAssessmentQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_CAREGIVER_NAME,
            value: undefined
          },
          {
            linkId:
              SupervisoryAssessmentQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_CAREGIVER_RELATION_TO_PATIENT,
            value: undefined
          }
        )
      }
    }

    return mutations
  }
}
